import { termoDaBusca } from '@/lib/texto';

/**
 * O estado da lista de lancamentos vive na URL (p.16).
 *
 * Aqui o motivo tem nome e sobrenome: "me manda os lancamentos com pendencia"
 * e uma frase que alguem diz no telefone as 7h da manha, e a resposta precisa
 * ser um link. Filtro que so existe na memoria da tela vira uma sequencia de
 * cliques que o outro tem que repetir, e ninguem repete.
 *
 * Os campos sao OPCIONAIS de proposito: `/combustivel/lancamento` continua
 * `/combustivel/lancamento`, e nao vira uma URL cheia de padrao que nao diz
 * nada a quem le.
 */

/**
 * O que o campo de busca ANUNCIA, e por isso tambem o que ele olha.
 *
 * Uma constante so, na mesma ordem em que a pagina monta a lista de campos. O
 * placeholder que promete menos do que o filtro casa produz linha que aparece
 * sem motivo visivel; o que promete mais produz busca que "nao funciona". As
 * duas versoes ja aconteceram aqui: o texto omitia `tipo` e `responsavel`, e o
 * comentario ao lado do filtro afirmava uma paridade que nao existia.
 */
export const CAMPOS_DA_BUSCA =
  'Equipamento, tipo, obra, operador, responsável, centro de custo ou tanque';

/**
 * O que a linha está esperando — as três respostas, na ordem em que importam.
 *
 * "Para conferir" (0122) é a primeira porque é a única com ação de quem lê:
 * consumo fora da faixa ainda sem assinatura, que se resolve olhando o número
 * e marcando o lançamento como conferido. As outras duas respondem à mesma
 * pergunta em grosso, e continuam valendo: "Com pendência" inclui o que falta
 * de cadastro (operador, centro de custo), que se resolve corrigindo.
 *
 * Um valor NOVO na lista é um valor novo na URL. O `ehFiltroDePendencia` é o
 * que impede `?pendencia=qualquercoisa` de virar filtro inventado, e a rota o
 * usa — por isso as duas coisas moram juntas neste arquivo.
 */
export const PENDENCIAS = ['Para conferir', 'Com pendência', 'Sem pendência'] as const;

export type FiltroDePendencia = (typeof PENDENCIAS)[number];

export function ehFiltroDePendencia(valor: unknown): valor is FiltroDePendencia {
  return typeof valor === 'string' && (PENDENCIAS as readonly string[]).includes(valor);
}

export interface BuscaLancamentos {
  readonly termo?: string;
  readonly pendencia?: FiltroDePendencia;
  readonly pagina?: number;
  readonly porPagina?: number;
}

export const PADRAO_POR_PAGINA = 10;
export const OPCOES_POR_PAGINA = [10, 25, 50, 100];

/**
 * A URL e entrada do usuario: qualquer coisa pode chegar ali, inclusive colada
 * a mao. Valor fora do vocabulario e DESCARTADO, nunca corrigido no chute —
 * adivinhar o que a pessoa quis dizer e como uma lista passa a mostrar coisa
 * diferente do que o filtro diz.
 */
export function validarBusca(entrada: Record<string, unknown>): BuscaLancamentos {
  const termo = entrada['termo'];
  const pendencia = entrada['pendencia'];
  const pagina = Number(entrada['pagina']);
  const porPagina = Number(entrada['porPagina']);

  /* Como foi digitado: aparar aqui comia o espaço a cada tecla (`termoDaBusca`). */
  const termoLimpo = termoDaBusca(termo);

  return {
    ...(termoLimpo ? { termo: termoLimpo } : {}),
    ...(ehFiltroDePendencia(pendencia) ? { pendencia } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...(OPCOES_POR_PAGINA.includes(porPagina) && porPagina !== PADRAO_POR_PAGINA
      ? { porPagina }
      : {}),
  };
}

/* ---------------------------------------------------------------------------
 * A LISTA de /combustivel.
 *
 * Ela nao tem filtro, so paginacao — e paginacao tambem e estado de visao: sem
 * ela a tela renderizava a frota inteira numa pagina so, e era a unica das tres
 * tabelas do sistema sem rodape, sem contagem e sem itens por pagina. A mesma
 * tabela se comportando de dois jeitos dentro do mesmo sistema e o que faz a
 * pessoa desconfiar de qual das duas esta quebrada.
 * ------------------------------------------------------------------------- */

export interface BuscaAbastecimentos {
  readonly pagina?: number;
  readonly porPagina?: number;
}

export function validarBuscaDaLista(entrada: Record<string, unknown>): BuscaAbastecimentos {
  const pagina = Number(entrada['pagina']);
  const porPagina = Number(entrada['porPagina']);

  return {
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...(OPCOES_POR_PAGINA.includes(porPagina) && porPagina !== PADRAO_POR_PAGINA
      ? { porPagina }
      : {}),
  };
}

export function montarBuscaDaLista(estado: {
  readonly pagina: number;
  readonly porPagina: number;
}): BuscaAbastecimentos {
  return {
    ...(estado.pagina > 1 ? { pagina: estado.pagina } : {}),
    ...(estado.porPagina !== PADRAO_POR_PAGINA ? { porPagina: estado.porPagina } : {}),
  };
}

/** Monta a busca para navegar, deixando de fora tudo que ja e o padrao. */
export function montarBusca(estado: {
  readonly termo: string;
  readonly pendencia: FiltroDePendencia | undefined;
  readonly pagina: number;
  readonly porPagina: number;
}): BuscaLancamentos {
  /* Como foi digitado, tambem na IDA: a tela grava a URL por aqui a cada tecla,
     e aparar comia o espaco ("CR- 001" virava "CR-001", robos no QAS, rodada 3). */
  const termo = termoDaBusca(estado.termo);

  return {
    ...(termo ? { termo } : {}),
    ...(estado.pendencia ? { pendencia: estado.pendencia } : {}),
    ...(estado.pagina > 1 ? { pagina: estado.pagina } : {}),
    ...(estado.porPagina !== PADRAO_POR_PAGINA ? { porPagina: estado.porPagina } : {}),
  };
}
